import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  updateDoc,
  doc,
  Timestamp,
  onSnapshot,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { AppNotification, NotificationType, NotificationData } from '../../types/notification';

const COLLECTION = 'notifications';

const toIso = (val: unknown): string => {
  if (!val) return new Date().toISOString();
  if (val instanceof Timestamp) return val.toDate().toISOString();
  return String(val);
};

const docToNotification = (id: string, data: Record<string, unknown>): AppNotification => ({
  id,
  userId: data.userId as string,
  type: data.type as NotificationType,
  title: data.title as string,
  body: data.body as string,
  titleParams: data.titleParams as Record<string, string> | undefined,
  bodyParams: data.bodyParams as Record<string, string> | undefined,
  data: data.data as NotificationData | undefined,
  read: (data.read as boolean) ?? false,
  createdAt: toIso(data.createdAt),
});

export class NotificationService {
  /**
   * NOTE: notifications are created exclusively server-side (Cloud Functions
   * triggers + the `sendSystemAnnouncement` callable). The `notifications`
   * collection is `create: if false` for clients, so this service is read-only
   * (get / count / mark-read / subscribe). [P1 #9]
   */

  /** Get notifications for a user */
  async getNotifications(userId: string, maxResults: number = 50): Promise<AppNotification[]> {
    const q = query(
      collection(db, COLLECTION),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      firestoreLimit(maxResults)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) =>
      docToNotification(d.id, d.data() as Record<string, unknown>)
    );
  }

  /** Get unread count for a user */
  async getUnreadCount(userId: string): Promise<number> {
    const q = query(
      collection(db, COLLECTION),
      where('userId', '==', userId),
      where('read', '==', false)
    );
    const snapshot = await getDocs(q);
    return snapshot.size;
  }

  /** Mark a single notification as read */
  async markAsRead(notificationId: string): Promise<void> {
    await updateDoc(doc(db, COLLECTION, notificationId), { read: true });
  }

  /** Mark all notifications as read for a user */
  async markAllAsRead(userId: string): Promise<void> {
    const q = query(
      collection(db, COLLECTION),
      where('userId', '==', userId),
      where('read', '==', false)
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return;

    const batch = writeBatch(db);
    snapshot.docs.forEach((d) => {
      batch.update(doc(db, COLLECTION, d.id), { read: true });
    });
    await batch.commit();
  }

  /** Real-time listener for unread count */
  subscribeToUnreadCount(
    userId: string,
    callback: (count: number) => void,
    onError?: (error: Error) => void
  ): () => void {
    const q = query(
      collection(db, COLLECTION),
      where('userId', '==', userId),
      where('read', '==', false)
    );
    return onSnapshot(
      q,
      (snapshot) => {
        callback(snapshot.size);
      },
      (error) => {
        console.error('[notifications] subscription error', error);
        onError?.(error);
      }
    );
  }

  /** Fetch all notifications (admin use, no userId filter), ordered by createdAt desc */
  async getAllNotifications(maxResults: number = 200): Promise<AppNotification[]> {
    const q = query(
      collection(db, COLLECTION),
      orderBy('createdAt', 'desc'),
      firestoreLimit(maxResults)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) =>
      docToNotification(d.id, d.data() as Record<string, unknown>)
    );
  }

  /** Fetch system_announcement notifications */
  async getSystemNotifications(maxResults: number = 100): Promise<AppNotification[]> {
    const q = query(
      collection(db, COLLECTION),
      where('type', '==', 'system_announcement'),
      orderBy('createdAt', 'desc'),
      firestoreLimit(maxResults)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) =>
      docToNotification(d.id, d.data() as Record<string, unknown>)
    );
  }

  /** Real-time listener for notifications list */
  subscribeToNotifications(
    userId: string,
    callback: (notifications: AppNotification[]) => void,
    maxResults: number = 50,
    onError?: (error: Error) => void
  ): () => void {
    const q = query(
      collection(db, COLLECTION),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      firestoreLimit(maxResults)
    );
    return onSnapshot(
      q,
      (snapshot) => {
        const notifications = snapshot.docs.map((d) =>
          docToNotification(d.id, d.data() as Record<string, unknown>)
        );
        callback(notifications);
      },
      (error) => {
        console.error('[notifications] subscription error', error);
        onError?.(error);
      }
    );
  }
}

export const notificationService = new NotificationService();
